'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { scrollToElementById } from '@/shared/utils/smoothScroll';

const SCROLL_RETRY_DELAY = 300;

const getHashTargetId = () => {
  if (!window.location.hash) {
    return null;
  }

  const hash = window.location.hash.slice(1);

  try {
    return decodeURIComponent(hash);
  } catch {
    return hash;
  }
};

export const HashScroll = () => {
  const pathname = usePathname();

  useEffect(() => {
    let frameId: number | null = null;
    let timeoutId: number | null = null;

    const clearScheduledScroll = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scrollToHash = (behavior: ScrollBehavior = 'auto') => {
      const targetId = getHashTargetId();

      if (!targetId) {
        return;
      }

      clearScheduledScroll();

      frameId = window.requestAnimationFrame(() => {
        scrollToElementById(targetId, { behavior });
        frameId = null;
      });

      timeoutId = window.setTimeout(() => {
        scrollToElementById(targetId, { behavior: 'auto' });
        timeoutId = null;
      }, SCROLL_RETRY_DELAY);
    };

    const handleHashChange = () => scrollToHash('smooth');
    const handleLoad = () => scrollToHash();

    scrollToHash();
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('load', handleLoad);

    return () => {
      clearScheduledScroll();
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('load', handleLoad);
    };
  }, [pathname]);

  return null;
};
